// The dashboard reads as two zones, and the split is the point:
//   1. תוצאות התקופה — figures that sum dated rows, so a month is a real slice.
//      The period control belongs to this section and nothing else.
//   2. מצב נוכחי — states of right now (what's owed, what's late, what's open,
//      the running partner balance). These ignore the selected period and say so,
//      because a month filter that silently skipped half the tiles would make the
//      untouched numbers look wrong.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAsync from '../../hooks/useAsync';
import { useLookups } from '../../context/LookupsContext';
import { dashboardApi } from '../../api';
import he from '../../i18n/he';
import { currentMonthStr } from '../../utils/dates';
import PageHeader from '../../components/PageHeader';
import AsyncBoundary from '../../components/Loader';
import StatTile from '../../components/StatTile';
import DataTable from '../../components/DataTable';
import Money, { formatMoney } from '../../components/Money';
import DateText from '../../components/DateText';
import PeriodPicker from './PeriodPicker';
import BalanceTopUnits from './BalanceTopUnits';

const overdueColumns = [
  { key: 'client_name', label: he.collections.client, primary: true },
  { key: 'job_title', label: he.collections.job },
  { key: 'issuing_partner_name', label: he.collections.issuer },
  { key: 'due_date', label: he.collections.dueDate, render: (r) => <DateText value={r.due_date} /> },
  { key: 'balance', label: he.collections.balance, render: (r) => <Money value={r.balance} /> },
];

const missingColumns = [
  { key: 'client_name', label: he.collections.client, primary: true },
  { key: 'title', label: he.jobs.jobTitle },
  // A retainer period is a month, so it renders without a day: 07/2026.
  { key: 'month', label: he.jobs.month, render: (r) => <DateText value={r.month} /> },
];

// Costs committed but not yet paid. A snapshot, like the outstanding invoices beside
// it: an expense owed since March is still owed in July. This list is the only place
// they surface as something to act on, since they are deliberately absent from the
// partner balance until the money actually goes out.
// Takes `lookups` because the category is an editable list, so only the label map can
// turn its stored code into Hebrew — hence a function where the others are constants.
const unpaidExpenseColumns = (lookups) => [
  { key: 'category', label: he.jobs.category, primary: true, render: (r) => lookups.categoryLabel(r.category) },
  { key: 'description', label: he.jobs.description, render: (r) => r.description || r.vendor || '—' },
  // Null for an office expense, which belongs to no job.
  { key: 'job_title', label: he.collections.job, render: (r) => r.job_title || he.officeExpenses.title },
  { key: 'client_name', label: he.collections.client, render: (r) => r.client_name || '—' },
  { key: 'amount', label: he.jobs.amount, render: (r) => <Money value={r.amount} /> },
];

// Which way the standing gap runs — never "X owes Y". The partners never transfer money
// to each other, so there is no debt and nothing to pay (see the settlements screen).
const BalanceLine = ({ balance }) => {
  if (!balance || !balance.amount) return <p className="muted">{he.dashboard.noGap}</p>;
  return (
    <p>
      {he.dashboard.gapFavours} <strong>{balance.favoursName}</strong>{' '}
      <Money value={balance.amount} className="" />{' '}
      <span className="muted">{`${he.dashboard.gapAgainst} ${balance.otherName}`}</span>
    </p>
  );
};

// Retainer revenue expected in a month still ahead of us. Deliberately NOT a StatTile:
// a number in a row of money tiles gets added up by the eye, and this one is neither
// profit nor a receivable. Its own well and its "תחזית" badge keep that plain.
const RetainerForecast = ({ forecast }) => (
  <div className="dash-forecast">
    <span>
      <strong>{he.dashboard.forecastTitle}</strong>{' '}
      <span className="badge gray">{he.dashboard.forecastBadge}</span>
    </span>
    {forecast.count > 0 ? (
      <span>
        <strong>{formatMoney(forecast.amount)}</strong>{' '}
        <span className="muted small-note">· {forecast.count} {he.dashboard.forecastJobs}</span>
      </span>
    ) : <span className="muted small-note">{he.dashboard.forecastEmpty}</span>}
  </div>
);

// Figures that sum dated rows over the chosen period. A section, not a card: the tiles
// are the surfaces, and a card around them was a box around boxes.
const PeriodSection = ({ data, period, onPeriodChange, refreshing }) => (
  <section className={refreshing ? 'refreshing' : ''}>
    <div className="section-head">
      <h2>{he.dashboard.periodSection}</h2>
      <PeriodPicker period={period} onChange={onPeriodChange} />
    </div>
    <div className="grid cols-3">
      <StatTile label={he.dashboard.totalProfit} value={formatMoney(data.totalProfit)} tone="green" />
      <StatTile label={he.dashboard.officeExpenses} value={formatMoney(data.officeExpenses)} tone="cost" />
      <StatTile label={he.dashboard.netAfterOffice} value={formatMoney(data.netAfterOffice)} tone="green" />
    </div>
    {/* Present only when a future month is selected — the server returns null
        otherwise, so a past or current month never shows a forecast. */}
    {data.retainerForecast && <RetainerForecast forecast={data.retainerForecast} />}
  </section>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const lookups = useLookups();
  // Defaults to this month rather than everything — the common question is "how
  // are we doing now", and all-time is one click away.
  const [period, setPeriod] = useState({ mode: 'month', month: currentMonthStr() });
  const { data, loading, refreshing, error } = useAsync(() => dashboardApi.summary(period), [period]);

  // Only the first load blanks the screen. Switching month keeps the previous
  // figures up (dimmed) so the picker doesn't vanish from under the click that
  // moved it. That rule now lives in useAsync, which every screen shares.
  return (
    <>
      <PageHeader title={he.dashboard.title} />
      <AsyncBoundary loading={loading} error={error}>
        {data && (
          <>
            <PeriodSection
              data={data}
              period={period}
              onPeriodChange={setPeriod}
              refreshing={refreshing}
            />

            <div className="section-head standalone">
              <h2>{he.dashboard.snapshotSection}</h2>
            </div>

            <div className="grid cols-4">
              <StatTile label={he.dashboard.outstanding} value={formatMoney(data.outstanding)} />
              <StatTile label={he.dashboard.overdue} value={data.overdue.count} tone="red" />
              {/* Money we owe, beside the money owed to us — a cost, so red like every cost. */}
              <StatTile
                label={he.dashboard.unpaidExpenses}
                value={formatMoney(data.unpaidExpenses.total)}
                tone="cost"
              />
              <StatTile label={he.dashboard.openTasks} value={data.openTasks} />
            </div>

            {data.missingInvoices && data.missingInvoices.count > 0 && (
              <div className="card dash-block">
                <h2 className="dash-alarm">
                  {he.dashboard.missingInvoices} ({data.missingInvoices.count})
                </h2>
                <DataTable
                  columns={missingColumns}
                  rows={data.missingInvoices.list}
                  onRowClick={(r) => navigate(`/jobs/${r.job_id}`)}
                />
              </div>
            )}

            <div className="card dash-block">
              <h2>{he.dashboard.balance}</h2>
              <BalanceLine balance={data.balance} />
              {/* Why it leans that way — the top few contributors only. The server
                  sends exactly these, never the full rows (dashboard/service.js). */}
              <BalanceTopUnits top={data.balanceTopUnits} />
            </div>

            <div className="card dash-block">
              <h2>{he.dashboard.overdueList}</h2>
              <DataTable columns={overdueColumns} rows={data.overdueList} empty={{ text: he.empty.overdue }} />
            </div>

            {/* Always rendered, not only when non-empty: an empty state that says
                "nothing is waiting to be paid" is itself the answer, and a card that
                appears and disappears is a card nobody learns to look for. */}
            <div className="card dash-block">
              <h2>{he.dashboard.unpaidExpensesList}</h2>
              <DataTable
                columns={unpaidExpenseColumns(lookups)}
                rows={data.unpaidExpenses.list}
                empty={{ text: he.empty.unpaidExpenses }}
                onRowClick={(r) => (r.job_id ? navigate(`/jobs/${r.job_id}`) : navigate('/expenses'))}
              />
            </div>
          </>
        )}
      </AsyncBoundary>
    </>
  );
}
